import {
  AuthTokenErrorAction,
  AuthTokenErrorType,
  AuthTokenSource,
  IAuthTokenError,
  IAuthTokenOptions,
  IAuthTokens,
  IAuthTokenStore,
  ISerializedAuthTokens,
  ITokenDebugger,
  TokenDependencies,
} from './authTokenTypes';
import { IWallClock } from '../../utils/time/IWallClock';
import { DefaultWallClock } from '../../utils/time/DefaultWallClock';
import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';
import { TokenDebugger } from './TokenDebugger';
import { isTokenError } from './isTokenError';
import { WriteableServerCookies } from '@portal/env/utils/serverCookies';

export class AuthTokens implements IAuthTokens {
  private readonly wallClock: IWallClock;
  private readonly debugger: ITokenDebugger;

  constructor(
    private readonly tokens: IAuthTokenStore[],
    options: IAuthTokenOptions = {},
  ) {
    this.wallClock = options.wallClock || DefaultWallClock.instance;
    this.debugger = options.debugger || new TokenDebugger();

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );
  }

  public async checkResponse(
    source: AuthTokenSource,
    response: Response,
    body?: string,
  ): Promise<AuthTokenErrorAction> {
    const token = this.findToken(source);

    if (!token) {
      // ??? don't know how this could happen but it's here
      return AuthTokenErrorAction.SUCCESS_CONTINUE;
    }

    const responseError = await token.checkResponse(response, body);

    if (responseError) {
      if (responseError.type === AuthTokenErrorType.UNAUTHORIZED) {
        return AuthTokenErrorAction.LOGOUT;
      }

      return AuthTokenErrorAction.SHOW_ERROR;
    }

    return AuthTokenErrorAction.SUCCESS_CONTINUE;
  }

  public async ensureAllTokensAreReady(): Promise<void> {
    if (!this.isLoggedIn) {
      return; // There's no way we can get the tokens we need so may as well stop here
    }

    this.debugger.addGlobalEvent({
      action: 'ensureAllTokensAreReadyStarted',
      message: 'Ensuring all tokens are ready',
    });

    try {
      const tokenNeedingRefresh = this.tokens.filter(
        (t) => !t.isReady || t.shouldRefresh,
      );
      const tokensWeCanRefresh = tokenNeedingRefresh.filter((t) =>
        this.canGetTokenFromDependencies(t),
      );

      while (tokensWeCanRefresh.length > 0) {
        const token = tokensWeCanRefresh[0];

        if (token.isReady && !token.shouldRefresh) {
          // This token is ready now - we can move on
          tokensWeCanRefresh.shift();
          continue;
        }

        try {
          await this.tryToUpdate(token);
        } finally {
          tokensWeCanRefresh.shift();
        }
      }
    } finally {
      this.debugger.addGlobalEvent({
        action: 'ensureAllTokensAreReadyFinished',
        message: 'Finished ensuring all tokens are ready',
      });
    }
  }

  public async getAccessToken(
    from: AuthTokenSource,
    forceRefetch?: boolean,
  ): Promise<string | IAuthTokenError> {
    const tokenProvider = this.findToken(from);

    if (!tokenProvider) {
      return {
        source: from,
        type: AuthTokenErrorType.OTHER,
        message: `No provider registered for ${from}`,
      };
    }

    const isFetching = !tokenProvider || !tokenProvider.isReady || forceRefetch;

    const canGetFromDependencies =
      this.canGetTokenFromDependencies(tokenProvider);
    const timeoutExpiry = tokenProvider.timeoutExpiry;
    const isTimedOut = timeoutExpiry && timeoutExpiry > this.wallClock.now();

    if (isFetching && !canGetFromDependencies) {
      return {
        source: from,
        type: AuthTokenErrorType.NO_TOKEN,
        message: `We don't have any suitable token for ${from}, nor could we get one`,
      };
    }

    if (isFetching && isTimedOut) {
      return {
        source: from,
        type: AuthTokenErrorType.REFRESH_BACKOFF,
        message: `Cannot refresh ${from} - waiting until ${timeoutExpiry?.toLocaleString(DateTime.TIME_WITH_SECONDS)} for the next attempt`,
      };
    }
    if (isFetching) {
      const refreshResult = await this.tryToUpdate(
        tokenProvider,
        [],
        forceRefetch,
      );
      if (isTokenError(refreshResult)) {
        return refreshResult;
      }

      if (!tokenProvider.isReady) {
        return {
          source: from,
          type: AuthTokenErrorType.OTHER,
          message: `Could not get a token for ${from}`,
        };
      }
    }
    return tokenProvider.value!;
  }

  async logout(): Promise<string[]> {
    let cookiesToClear: string[] = [];
    for (const token of this.tokens) {
      if (token.logoutCallback) {
        await token.logoutCallback();
      }
      cookiesToClear = [...cookiesToClear, ...token.usedCookieNames];
    }

    return cookiesToClear;
  }

  public saveCookies(
    writeableCookies: WriteableServerCookies,
  ): WriteableServerCookies {
    for (const token of this.tokens) {
      token.saveCookies(writeableCookies);
    }
    return writeableCookies;
  }

  public bootstrapWith(
    source: AuthTokenSource,
    value: string,
    expiryDate?: string,
  ): void {
    const token = this.findToken(source);

    if (!token) {
      return;
    }

    token.update({
      source,
      value,
      expiryDate: expiryDate ? DateTime.fromISO(expiryDate) : null,
    });
  }

  public get serialized(): ISerializedAuthTokens {
    const results: Partial<ISerializedAuthTokens> = {};

    for (const token of this.tokens) {
      results[token.source] = {
        token: token.value,
        expiryDate: token.expiryDate?.toISO() || null,
        source: token.source,
        meta: token.meta,
      };
    }

    return results as ISerializedAuthTokens;
  }

  public get isLoggedIn(): boolean {
    const notReadyTokens = this.tokens.filter(
      (t) => !t.isReady && t.isRequired,
    );

    const notRefreshableTokens: IAuthTokenStore[] = [];

    for (const token of notReadyTokens) {
      if (!this.canGetTokenFromDependencies(token) || !token.canAttemptAgain) {
        notRefreshableTokens.push(token);
      }
    }

    return notRefreshableTokens.length === 0;
  }

  public get lastTokenValues() {
    const results: Record<AuthTokenSource, string | null> = {} as Record<
      AuthTokenSource,
      string | null
    >;

    for (const token of this.tokens) {
      results[token.source] = token.isReady ? token.value : null;
    }

    return results;
  }

  get isPendingRefresh(): boolean {
    // If there's anything that we're waiting on, we're pending refresh
    const missingOrNeedsRefresh = this.tokens.filter(
      (t) => !t.isReady || t.shouldRefresh,
    );

    // Need to make sure we actually *can* refresh
    const refreshableTokens: IAuthTokenStore[] = [];

    for (const token of missingOrNeedsRefresh) {
      const timeoutExpiry = token.timeoutExpiry;

      if (timeoutExpiry && timeoutExpiry > this.wallClock.now()) {
        // We're still waiting for the timeout to expire
        continue;
      }
      // See if we can get the token from any of its dependencies
      if (this.canGetTokenFromDependencies(token)) {
        refreshableTokens.push(token);
      }
    }

    return refreshableTokens.length > 0;
  }

  private canGetTokenFromDependencies(
    token: IAuthTokenStore,
    hasCheckedDependencies: AuthTokenSource[] = [],
  ): boolean {
    const dependencies = token.dependencies;
    const availableDependencies: AuthTokenSource[] = [];

    for (const dependency of dependencies) {
      const alreadyChecked = hasCheckedDependencies.includes(dependency);
      if (alreadyChecked) {
        continue;
      }

      const dependencyToken = this.findToken(dependency);
      const exists = !!dependencyToken;

      const isAlreadyReady = exists && dependencyToken?.isReady;
      const canGetFromDependencies =
        exists &&
        this.canGetTokenFromDependencies(dependencyToken!, [
          ...hasCheckedDependencies,
          token.source,
        ]);

      if (isAlreadyReady || canGetFromDependencies) {
        availableDependencies.push(dependency);
      }
    }

    return availableDependencies.length > 0;
  }
  private findToken(source: AuthTokenSource): IAuthTokenStore | undefined {
    return this.tokens.find((t) => t.source === source);
  }

  /**
   * Recursively tries to update the token with the dependencies it needs
   *
   * If a dependency also needs updating, we will try to update that too.
   * @param token The token to update
   * @param alreadyTriedToRefresh A list of tokens that we've already tried to refresh - used to avoid infinite loops
   * @param forceRefresh If true, will force a refresh even if the token doesn't need it
   * @private
   */
  private async tryToUpdate(
    token: IAuthTokenStore,
    alreadyTriedToRefresh: AuthTokenSource[] = [],
    forceRefresh: boolean = false,
  ): Promise<null | IAuthTokenError> {
    const dependencies = token.dependencies;
    const dependencyTokens = dependencies
      .map((d) => this.findToken(d))
      .filter((t) => t !== undefined) as IAuthTokenStore[];

    // These are the dependencies that are ready and don't need refreshing or anything
    const validDependencies = dependencyTokens.filter((t) => t.isReady);

    const dependenciesNeedingRefresh = dependencyTokens.filter(
      (t) => !t.isReady,
    );

    if (dependenciesNeedingRefresh.length > 0) {
      // Some dependencies need refreshing - recurse down to them first
      for (const dependency of dependenciesNeedingRefresh) {
        if (
          alreadyTriedToRefresh.includes(dependency.source) ||
          dependency.isReady
        ) {
          // We already tried to refresh this one - skip it to avoid infinite loops
          continue;
        }

        await this.tryToUpdate(dependency, [
          ...alreadyTriedToRefresh,
          token.source,
        ]);

        if (dependency.isReady) {
          validDependencies.push(dependency);
        }
      }
    }

    if (validDependencies.length === 0) {
      // We totally failed to get the dependencies we need
      return {
        source: token.source,
        type: AuthTokenErrorType.NO_TOKEN,
        message: `Could not get dependencies for ${token.source}`,
      };
    }
    // WE might have already tried the token or refreshed it if it's a dependency of itself
    if (token.canAttemptAgain) {
      if (forceRefresh || !token.isReady || token.shouldRefresh) {
        return await token.updateWith(
          this.tokenListToDependencies(validDependencies),
        );
      } else {
        return null; // We already refreshed. Let's goooo
      }
    } else {
      return {
        source: token.source,
        type: AuthTokenErrorType.MAX_ATTEMPTS_EXCEEDED,
        message: `Could not refresh ${token.source} - max attempts exceeded`,
      };
    }
  }

  private tokenListToDependencies(
    dependencyTokens: (IAuthTokenStore | undefined)[],
  ): TokenDependencies {
    const dependencies: TokenDependencies = {};

    for (const token of dependencyTokens) {
      if (token) {
        dependencies[token.source] = {
          source: token.source,
          value: token.value!,
          expiryDate: token.expiryDate,
        };
      }
    }
    return dependencies;
  }

  getSourceMeta<T>(source: AuthTokenSource): T | null {
    const store = this.findToken(source);
    return (store?.meta as T) || null;
  }
}

export const emptyAuthTokens = new AuthTokens([]);
